import styled from '@emotion/styled'
import debug from 'debug'
import React, { FC, useMemo, useState } from 'react'
import { calcRem } from '../../..'

export const liveEventDebug = debug('event')

export type DebugEvent = { title: string; details?: unknown; timeStamp: number }

export interface DebugPanelProps {
    debugObj: unknown
    eventFeed: DebugEvent[]
    onHide: VoidFunction
    hidePanel: boolean
}

export const isDebuggingEvents =
    typeof window !== 'undefined' &&
    !!window.localStorage.getItem('debug')?.includes('event')

export const InternalDebugPanel: FC<DebugPanelProps> = ({
    debugObj,
    eventFeed,
    onHide,
    hidePanel,
}) => {
    const sortedEventFeed = useMemo(
        () => eventFeed.sort((a, b) => b.timeStamp - a.timeStamp),
        [eventFeed],
    )

    return (
        <StyledDebugPanelContainer>
            <StyledDebugPanel>
                <StyledHideButton onClick={onHide}>Toggle</StyledHideButton>
                {!hidePanel && (
                    <>
                        {JSON.stringify(debugObj, null, 2)}
                        <h4>Latest events:</h4>
                        <StyledDebugEventsContainer>
                            {sortedEventFeed.map((event, i) => (
                                <details key={i}>
                                    <summary>{event.title}</summary>
                                    {event.details && (
                                        <pre>
                                            {JSON.stringify(
                                                event.details,
                                                null,
                                                2,
                                            )}
                                        </pre>
                                    )}
                                </details>
                            ))}
                        </StyledDebugEventsContainer>
                    </>
                )}
            </StyledDebugPanel>
        </StyledDebugPanelContainer>
    )
}

export const useDebugPanel = ({ debugObj }: { debugObj: unknown }) => {
    const [eventFeed, setEventFeed] = useState<DebugEvent[]>([])

    const addDebugEvent = (
        title: DebugEvent['title'],
        details?: DebugEvent['details'],
    ) => {
        liveEventDebug(title, details)
        setEventFeed((prev) => [
            ...prev,
            { title, details, timeStamp: Date.now() },
        ])
    }

    const [hidePanel, setHidePanel] = useState(false)

    const DebugPanel = () => (
        <InternalDebugPanel
            eventFeed={eventFeed}
            debugObj={debugObj}
            hidePanel={hidePanel}
            onHide={() => setHidePanel(!hidePanel)}
        />
    )

    return { DebugPanel, addDebugEvent }
}

export const StyledDebugPanelContainer = styled('div')(({ theme }) => ({
    position: 'absolute',
    height: 0,
    width: 0,
}))

export const StyledDebugEventsContainer = styled('div')(({ theme }) => ({
    maxHeight: '20vh',
    overflow: 'scroll',

    '> details': {
        padding: 5,
        borderBottom: `1px solid`,
    },
}))

export const StyledDebugPanel = styled('pre')(({ theme }) => ({
    backgroundColor: '#2a303c',
    color: '#bfc5cf',
    borderRadius: calcRem(5),
    boxShadow: '-4px 5px 10px 3px #0000004d',
    position: 'fixed',
    top: 20,
    right: 20,
    marginLeft: 'auto',
    left: '66vw',
    width: '33vw',
    zIndex: 10000,
    fontSize: 13,
}))

export const StyledHideButton = styled('button')(({ theme }) => ({
    position: 'absolute',
    right: 0,
    top: 0,
}))
